// exceptionsApi — typed CRUD for /v1/schedules/exceptions (Phase 1C).
// Holidays, typhoons, ad-hoc closures, extra-coverage days. Used by
// the dashboard month calendar to overlay coloured ribbons on cells.
import { apiFetch } from "../../lib/api-client.js";

export type ExceptionKind = "holiday" | "typhoon" | "closure" | "extra_coverage" | "custom";

export interface ScheduleException {
  id: string;
  tenant_id: string;
  site_id: string | null;
  exception_date: string;       // YYYY-MM-DD
  extends_until: string | null;
  kind: ExceptionKind;
  auto_skip: number;            // 0 / 1
  label: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  site_name: string | null;
}

export interface ExceptionsListResponse { items: ScheduleException[]; correlation_id: string | null }

export class ExceptionsApiError extends Error {
  status: number; code: string; correlationId: string | null; fields?: string[]; upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, fields?: string[], upstreamMessage?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.fields = fields; this.upstreamMessage = upstreamMessage;
  }
}

export function friendlyExceptionsError(err: unknown): string {
  if (err instanceof ExceptionsApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能修改例外日。";
    if (err.status === 404) return "找不到此例外日記錄。";
    if (err.status === 409 && err.code === "duplicate_exception")
      return "這天的例外日已存在（同一案場 / 同一日期不能重複）。";
    if (err.status === 400 && err.code === "site_not_found") return "找不到指定的案場。";
    if (err.status === 400 && err.code === "invalid_body") {
      return `欄位填寫不完整：${(err.fields ?? []).join("、")}。`;
    }
    if (err.status === 400) return "資料格式不正確。";
    const msgPart = err.upstreamMessage ? `・msg=${err.upstreamMessage}` : "";
    return `例外日資料暫時無法存取（HTTP ${err.status}・code=${err.code}${msgPart}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<ExceptionsApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let fields: string[] | undefined; let msg: string | undefined;
  try { const b = (await r.json()) as { error?: string; correlation_id?: string; fields?: string[]; message?: string }; code = b.error ?? code; cid = b.correlation_id ?? cid; fields = b.fields; msg = b.message; } catch {}
  return new ExceptionsApiError(r.status, code, cid, fields, msg);
}

export interface ExceptionsQuery {
  tenantId?: string | null;
  from?: string | null;
  to?: string | null;
  /** site_id, or "_tenant_only" to exclude site-scoped rows. */
  siteId?: string | null;
}

export async function listExceptions(params: ExceptionsQuery = {}): Promise<ExceptionsListResponse> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.siteId) qs.set("site_id", params.siteId);
  const r = await apiFetch(`/v1/schedules/exceptions${qs.toString() ? `?${qs}` : ""}`);
  if (!r.ok) throw await asError(r);
  return (await r.json()) as ExceptionsListResponse;
}

export interface CreateExceptionBody {
  exception_date: string;
  label: string;
  kind?: ExceptionKind;
  site_id?: string | null;
  extends_until?: string | null;
  auto_skip?: boolean;
  notes?: string | null;
}

export async function createException(body: CreateExceptionBody, tenantId?: string | null): Promise<{ id: string }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/exceptions${qs}`, {
    method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export type PatchExceptionBody = Partial<{
  label: string; notes: string | null; kind: ExceptionKind;
  auto_skip: boolean; extends_until: string | null;
}>;

export async function patchException(id: string, body: PatchExceptionBody, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/exceptions/${encodeURIComponent(id)}${qs}`, {
    method: "PATCH", headers: { "content-type": "application/json" }, body: JSON.stringify(body),
  });
  if (!r.ok) throw await asError(r);
}

export async function deleteException(id: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/schedules/exceptions/${encodeURIComponent(id)}${qs}`, { method: "DELETE" });
  if (!r.ok) throw await asError(r);
}
